// import {Customer} from "../model/customer";
import firebase from "firebase/app";
import "firebase/auth";
import { entity } from "../../infra/api/rpc/api";
import AuthFlowType from "../../presentation/auth_flow";
import { AuthStateType } from "../../presentation/auth_state_type";
import { AppError, ErrorType } from "../model/app_error";
import { User } from "../model/user";
import {
  ICustomerRepository,
  TCreateCustomerRequest,
} from "../repository/customer_repository";
import { IUserRepository } from "../repository/user_repository";
import { handleErrorFirebaseAuth } from "./error_handler_firebase";

interface IAuthUseCase {
  onAuthListener(
    callback: (
      error: any,
      data?: { user: User | null; authState: AuthStateType }
    ) => void
  ): void;

  offAuthListener(): void;

  createCustomer(
    createCustomerRequest: TCreateCustomerRequest,
    email: string,
    password: string
  ): Promise<User>;

  login(email: string, password: string): Promise<void>;

  logout(): Promise<void>;

  updatePassword(currentPassword: string, newPassword: string): Promise<void>;

  updateEmail(currentPassword: string, email: string): Promise<void>;

  sendPasswordResetEmail(email: string): Promise<void>;
}

class AuthUseCase implements IAuthUseCase {
  private fireBaseListener: firebase.Unsubscribe;

  private authFlow: AuthFlowType = AuthFlowType.UNKNOWN;

  constructor(
    readonly userRepository: IUserRepository,
    readonly customerRepository: ICustomerRepository
  ) {}

  public onAuthListener(
    callback: (
      error: any,
      data?: { user: User | null; authState: AuthStateType }
    ) => void
  ): void {
    // onAuthStateChanged解除用にローカルに戻り値を保存しておく
    this.fireBaseListener = firebase.auth().onAuthStateChanged(
      (authUser: firebase.User | null) => {
        if (this.authFlow !== AuthFlowType.CUSTOMER_CREATE && authUser) {
          this.userRepository
            .getMe()
            .then((user: User): void => {
              callback(null, {
                user,
                authState: AuthStateType.LOGIN_USER,
              });
            })
            .catch((error) => {
              // getMeがエラー時の処理
              firebase
                .auth()
                .signOut()
                .then(() => {
                  callback(error);
                });
            });
        } else {
          // ログオンしていない状態のUserを返す
          callback(null, {
            user: new User("UNKNOWN USER", 0, null, null, [], false),
            authState: AuthStateType.UNKNOWN,
          });
        }
      },
      (error: any) => {
        throw handleErrorFirebaseAuth(error);
      }
    );
  }

  public offAuthListener(): void {
    this.fireBaseListener();
  }

  public createCustomer(
    createCustomerRequest: TCreateCustomerRequest,
    email: string,
    password: string
  ): Promise<User> {
    return new Promise<User>((resolve, reject) => {
      // step0 stop observer => backend
      this.authFlow = AuthFlowType.CUSTOMER_CREATE;

      //  step1 firebase user logout
      firebase
        .auth()
        .signOut()
        .then((): Promise<void> => {
          //  step2 firebase user create
          return this.createCustomerStep2(email, password);
        })
        .then((): Promise<void> => {
          //    step3 backend user create
          switch (createCustomerRequest.type) {
            case entity.Customer.Type.Type_Expert:
              switch (createCustomerRequest.campaign) {
                case entity.Customer.Campaign.Campaign_Normal:
                  return this.createCustomerStep3(createCustomerRequest);
                case entity.Customer.Campaign.Campaign_SuperEngineer:
                  return this.createSuperEngineerStep3(createCustomerRequest);
              }
            case entity.Customer.Type.Type_Pro:
              return this.createProStep3(createCustomerRequest);
            default:
              throw new Error();
          }
        })
        .then((): Promise<User> => {
          //    step4 get User entity
          return this.userRepository.getMe();
        })
        .then((user: User): void => {
          //    step5 start observer => backend
          this.authFlow = AuthFlowType.UNKNOWN;
          resolve(user);
        })
        .catch((error: any) => {
          //    errorハンドリング
          this.authFlow = AuthFlowType.UNKNOWN;
          reject(handleErrorFirebaseAuth(error));
        });
    });
  }

  public login(email: string, password: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      firebase
        .auth()
        .setPersistence(firebase.auth.Auth.Persistence.LOCAL)
        .then((): Promise<firebase.auth.UserCredential> => {
          return firebase.auth().signInWithEmailAndPassword(email, password);
        })
        .then((): void => {
          resolve();
        })
        .catch((error: any): void => {
          reject(handleErrorFirebaseAuth(error));
        });
    });
  }

  public logout(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      firebase
        .auth()
        .signOut()
        .then((): void => {
          resolve();
        })
        .catch((error: any) => {
          reject(handleErrorFirebaseAuth(error));
        });
    });
  }

  public updatePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const user = firebase.auth().currentUser;
      // 再認証のcredentialを取得する
      if (!user) {
        throw new AppError(
          "UNKNOWN CODE",
          ErrorType.CLIENT_ERROR,
          "異常が発生しました"
        );
      }
      if (!user.email) {
        throw new AppError(
          "UNKNOWN CODE",
          ErrorType.CLIENT_ERROR,
          "異常が発生しました"
        );
      }
      const credential = firebase.auth.EmailAuthProvider.credential(
        user.email,
        currentPassword
      );

      // APIの使用上最新の認証状態が必要な為再認証を実行御処理をする
      user
        .reauthenticateWithCredential(credential)
        .then(() => {
          // 処理の実行
          return user.updatePassword(newPassword);
        })
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(handleErrorFirebaseAuth(error));
        });
    });
  }

  public updateEmail(currentPassword: string, email: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const user = firebase.auth().currentUser;
      // 再認証のcredentialを取得する
      if (!user) {
        throw new AppError(
          "UNKNOWN CODE",
          ErrorType.CLIENT_ERROR,
          "異常が発生しました"
        );
      }
      if (!user.email) {
        throw new AppError(
          "UNKNOWN CODE",
          ErrorType.CLIENT_ERROR,
          "異常が発生しました"
        );
      }
      const credential = firebase.auth.EmailAuthProvider.credential(
        user.email,
        currentPassword
      );

      // APIの使用上最新の認証状態が必要な為再認証を実行御処理をする
      user
        .reauthenticateWithCredential(credential)
        .then(() => {
          // 処理の実行
          return user.updateEmail(email);
        })
        .then(() => {
          resolve();
        })
        .catch((error: any) => {
          reject(handleErrorFirebaseAuth(error));
        });
    });
  }

  public sendPasswordResetEmail(email: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const auth = firebase.auth();

      auth
        .sendPasswordResetEmail(email)
        .then(() => {
          // Email sent.
          resolve();
        })
        .catch((error: any) => {
          reject(handleErrorFirebaseAuth(error));
        });
    });
  }

  private createCustomerStep2(email: string, password: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // firebase user create

      firebase
        .auth()
        .createUserWithEmailAndPassword(email, password)
        .then((): void => {
          resolve();
        })
        .catch((error: any): void => {
          reject(handleErrorFirebaseAuth(error));
        });
    });
  }

  private createCustomerStep3(
    createCustomerRequest: TCreateCustomerRequest
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // backend user create

      this.customerRepository
        .create(createCustomerRequest)
        .then((): void => {
          resolve();
        })
        .catch((error: any) => {
          this.customerRepository
            .create(createCustomerRequest)
            .then((): void => {
              resolve();
            })
            .catch((error1: any): void => {
              reject(error1);
            });
        });
    });
  }

  private createSuperEngineerStep3(
    createCustomerRequest: TCreateCustomerRequest
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.customerRepository
        .createSuperEngineer(createCustomerRequest)
        .then((): void => {
          resolve();
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }

  private createProStep3(
    createCustomerRequest: TCreateCustomerRequest
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.customerRepository
        .createPro(createCustomerRequest)
        .then((): void => {
          resolve();
        })
        .catch((error: any) => {
          reject(error);
        });
    });
  }
}

const createAuthUseCase = (
  userRepository: IUserRepository,
  customerRepository: ICustomerRepository
): IAuthUseCase => {
  return new AuthUseCase(userRepository, customerRepository);
};

export { IAuthUseCase, createAuthUseCase };



// WEBPACK FOOTER //
// ./src/domain/usecase/auth_usecase.ts